import React, { useRef, useState } from "react";
import { Field } from "formik";
import DatePicker, { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale";
import { differenceInCalendarDays, isValid, parse } from "date-fns";
import InputError from "../../common/input-error/InputError";
import { withTranslation } from "../../../i18n";
import Tooltip from "../../Tooltip/Tooltip";
import BasicSelect from "../basic-select/BasicSelect";
import css from "./InputDate.module.scss";

registerLocale("fr", fr);

const namespacesRequired = ['common'];

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === "function") {
        const d = value.toDate();
        return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

const MONTHS_FR = [
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const getYearsArray = (minDate, maxDate) => {
    const now = new Date().getFullYear();
    const begin = minDate ? minDate.getFullYear() : now - 115;
    const end = maxDate ? maxDate.getFullYear() : now + 115;
    const res = [];
    for (let i = end; i >= begin; i--) res.push(i);
    return res;
};

// Convert moment's display tokens to date-fns tokens.
const toDateFnsFormat = (fmt) =>
    (fmt || "DD/MM/YYYY").replace(/DD/g, "dd").replace(/YYYY/g, "yyyy");

// Keeps only the 8 digits of a jj/mm/aaaa date and re-inserts the separators as
// the user types, so "10102027" becomes "10/10/2027" instead of being handed to
// react-datepicker as a bare number. GL-5227
export const maskFrenchDate = (raw) => {
    const digits = String(raw ?? "").replace(/\D/g, "").slice(0, 8);
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
        .filter((part) => part.length > 0)
        .join("/");
};

// react-datepicker reads event.target.value *after* onChangeRaw, so rewriting it
// here is what applies the mask. It also fires onChangeRaw when a day is picked
// in the calendar — that event targets a <div>, leave it alone. GL-5227
const applyMaskOnRawChange = (event) => {
    const target = event?.target;
    if (!target || target.tagName !== "INPUT") return;
    target.value = maskFrenchDate(target.value);
};

// Numeric-only input limited to jj/mm/aaaa. `strictParsing` keeps a partial entry
// like "10/10/20" from being coerced into a date (without it react-datepicker
// falls back to `new Date(value)`, which read "10102027" as the year 101020).
const MASKED_DATE_PROPS = {
    strictParsing: true,
    customInput: <input type="text" inputMode="numeric" maxLength={10} />,
};

const parseCompleteDate = (value, dateFormat) => {
    const parsed = parse(value, dateFormat, new Date());
    return isValid(parsed) && parsed.getFullYear() >= 1000 ? parsed : null;
};

const isOutOfBounds = (date, minDate, maxDate) =>
    (!!minDate && differenceInCalendarDays(date, minDate) < 0)
    || (!!maxDate && differenceInCalendarDays(date, maxDate) > 0);

const clampToBounds = (date, minDate, maxDate) => {
    if (!date) return date;
    if (minDate && differenceInCalendarDays(date, minDate) < 0) return minDate;
    if (maxDate && differenceInCalendarDays(date, maxDate) > 0) return maxDate;
    return date;
};

const InputDate = (props) => {
    const datepickerRef = useRef(null);
    const minDate = toDate(props.inputMinDate);
    const maxDate = toDate(props.inputMaxDate);
    const initialDate = toDate(props.inputInitialDate);
    const years = getYearsArray(minDate, maxDate);
    const dateFormat = toDateFnsFormat(props.inputDateFormat);

    const renderCustomHeader = ({ date, changeYear, changeMonth }) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
            <BasicSelect
                customClass={css.month_year_select}
                options={MONTHS_FR.map((label, value) => ({ value, label }))}
                value={date.getMonth()}
                onChange={(val) => changeMonth(val.value)}
            />
            <BasicSelect
                customClass={css.month_year_select}
                options={years.map((y) => ({ value: y, label: y }))}
                value={date.getFullYear()}
                onChange={(val) => changeYear(val.value)}
            />
        </div>
    );

    return (
        <Field name={props.inputName} id={props.inputId}>
            {({ field: { value }, form: { setFieldValue, setFieldTouched, touched, errors } }) => {
                const selected = toDate(value);
                const hasError = !props.hideErrors && errors[props.inputName] && touched[props.inputName];
                const commitDate = (date) => {
                    setFieldValue(props.inputName, date || null, true);
                    if (touched[props.inputName]) {
                        setFieldTouched(props.inputName, true, false);
                    }
                };
                const handleRawChange = (event) => {
                    const target = event?.target;
                    if (!target || target.tagName !== "INPUT") return;
                    if (props.numericMask) applyMaskOnRawChange(event);
                    const typed = parseCompleteDate(target.value, dateFormat);
                    if (typed && isOutOfBounds(typed, minDate, maxDate)) commitDate(typed);
                };

                return (
                    <div className={`${css.input__wrapper} ${props.customClass || ''}`} ref={props.anchorRef}>
                        {props.label && (
                            <label className={`${css.label} ${props.showTooltip ? css.label_inline_block : ''}`}>
                                {props.label} {props.required && "*"}
                            </label>
                        )}
                        {props.showTooltip && (
                            <div className={css.test}>
                                <Tooltip
                                    customClass={css.custom__tooltip}
                                    triggerComponent={<span className={css.tooltip_orange}>?</span>}
                                    trigger="hover"
                                >
                                    {props.tooltipContent}
                                </Tooltip>
                            </div>
                        )}

                        <div
                            className={`
                                ${css.input__date__wrapper}
                                ${props.customClass || ''}
                                ${props.customDatepicker || ''}
                                ${hasError ? css.border__error : css.border__no_error}
                            `}
                        >
                            <DatePicker
                                ref={datepickerRef}
                                selected={selected}
                                onChange={commitDate}
                                onChangeRaw={handleRawChange}
                                onCalendarClose={() => {
                                    props.hideErrors && props.makeErrorsVisible?.();
                                    setTimeout(() => setFieldTouched(props.inputName, true), 0);
                                }}
                                openToDate={clampToBounds(selected || initialDate, minDate, maxDate) || undefined}
                                minDate={minDate || undefined}
                                maxDate={maxDate || undefined}
                                dateFormat={dateFormat}
                                placeholderText={props.inputPlaceholder}
                                locale={props.i18n.language?.startsWith("fr") ? "fr" : undefined}
                                disabled={props.disabled}
                                shouldCloseOnSelect
                                preventOpenOnFocus
                                showMonthDropdown={!props.selectableMonthYear}
                                showYearDropdown={!props.selectableMonthYear}
                                dropdownMode="select"
                                renderCustomHeader={props.selectableMonthYear ? renderCustomHeader : undefined}
                                wrapperClassName={css.datepicker__wrapper}
                                className={css.datepicker__input}
                                autoComplete="off"
                                {...(props.numericMask ? MASKED_DATE_PROPS : {})}
                                {...(props.datePickerProps || {})}
                            />
                            <button
                                type="button"
                                className={css.input__date__icon}
                                onClick={() => datepickerRef.current?.setOpen(true)}
                            >
                                <img src="/static/images/pictos/icon-calendar.svg" height={"12px"} width={"12px"} />
                            </button>
                        </div>
                        {hasError && <InputError name={props.inputName} />}
                    </div>
                );
            }}
        </Field>
    );
};

export default withTranslation(namespacesRequired)(InputDate);
